import {
  useGame,
  usePlayer,
} from "@empirica/core/player/classic/react";
import React, { useLayoutEffect, useRef, useState } from "react";
import { Button } from "../components/Button";
import { TestContinueWithoutInteraction } from "../components/TestContinueWithoutInteraction";
import { getCounterpartCopy } from "../payoffFrame";

const outcomeLikertOptions = [
  { value: "0", label: "Not at all" },
  { value: "1", label: "Slightly" },
  { value: "2", label: "Moderately" },
  { value: "3", label: "Very" },
  { value: "4", label: "Extremely" },
];

export function ExitSurvey({ next }) {
  const game = useGame();
  const player = usePlayer();
  const pageTopRef = useRef(null);
  const hasPartnerChat = Number(game?.get("negotiationChatMessageCount") ?? 0) > 0;
  const counterpartLabel = getCounterpartCopy(
    game?.get("design") || player?.get("queueTreatment") || {}
  ).term;

  const labelClassName = "block text-2xl font-medium text-gray-700 my-2";
  const inputClassName =
    "appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-empirica-500 focus:border-empirica-500 text-xl";

  const [generalFeedback, setGeneralFeedback] = useState("");
  const [finalOutcomeFairness, setFinalOutcomeFairness] = useState("");
  const [partnerFairness, setPartnerFairness] = useState("");
  const [chatCivility, setChatCivility] = useState("");
  const [chatUsefulness, setChatUsefulness] = useState("");
  const [chatbotFairness, setChatbotFairness] = useState("");
  const [chatbotUsefulness, setChatbotUsefulness] = useState("");
  const [surveyPage, setSurveyPage] = useState(1);

  const outcomeRatingItems = [
    {
      name: "finalOutcomeFairness",
      label: "How fair was the final outcome?",
      value: finalOutcomeFairness,
      setValue: setFinalOutcomeFairness,
    },
    {
      name: "partnerFairness",
      label: `How reasonable was your ${counterpartLabel}?`,
      value: partnerFairness,
      setValue: setPartnerFairness,
    },
    ...(hasPartnerChat
      ? [
          {
            name: "chatUsefulness",
            label: `How useful was the chat with your ${counterpartLabel}?`,
            value: chatUsefulness,
            setValue: setChatUsefulness,
          },
          {
            name: "chatCivility",
            label: `How civil was the chat with your ${counterpartLabel}?`,
            value: chatCivility,
            setValue: setChatCivility,
          },
        ]
      : []),
    {
      name: "chatbotUsefulness",
      label: "How useful was the chatbot?",
      value: chatbotUsefulness,
      setValue: setChatbotUsefulness,
    },
    {
      name: "chatbotFairness",
      label: "How impartial was the chatbot?",
      value: chatbotFairness,
      setValue: setChatbotFairness,
    },
  ];
  const hasCoreLikertRatings =
    finalOutcomeFairness &&
    partnerFairness &&
    (!hasPartnerChat || (chatUsefulness && chatCivility));
  const hasChatbotLikertRatings = chatbotUsefulness && chatbotFairness;
  const canAdvanceFromLikertPage = hasCoreLikertRatings && hasChatbotLikertRatings;

  useLayoutEffect(() => {
    pageTopRef.current?.scrollIntoView({ block: "start", inline: "nearest" });
  }, [surveyPage]);

  function handleSubmit(event) {
    event.preventDefault();
    player.set("reportForm", {
      finalOutcomeFairness,
      partnerFairness,
      chatCivility: hasPartnerChat ? chatCivility : null,
      chatUsefulness: hasPartnerChat ? chatUsefulness : null,
      chatbotFairness,
      chatbotUsefulness,
      generalFeedback,
    });
    next();
  }

  function handleContinueWithoutInteraction() {
    if (surveyPage < 2) {
      setSurveyPage((page) => page + 1);
      return;
    }

    next();
  }

  return (
    <div
      ref={pageTopRef}
      className={`py-8 mx-auto px-8 space-y-6 ${
        surveyPage === 1 ? "max-w-[1070px]" : "max-w-4xl"
      }`}
    >
      <form className="space-y-6" onSubmit={handleSubmit}>
        {surveyPage === 1 ? (
          <div className="rounded-md border border-gray-200 p-5 space-y-6">
            <p className="text-2xl font-semibold text-gray-800">
              Please rate each item.
            </p>

            <div className="overflow-x-auto">
              <div className="min-w-[900px] space-y-3">
                <div className="grid grid-cols-[minmax(320px,1fr)_repeat(5,minmax(100px,0.45fr))] items-end gap-x-0">
                  <div />
                  {outcomeLikertOptions.map((option) => (
                    <div
                      key={`outcome-rating-heading-${option.value}`}
                      className="px-2 text-center text-lg font-semibold leading-tight text-gray-700"
                    >
                      {option.label}
                    </div>
                  ))}
                </div>

                {outcomeRatingItems.map((item) => (
                  <div
                    key={item.name}
                    className="grid grid-cols-[minmax(320px,1fr)_repeat(5,minmax(100px,0.45fr))] items-center gap-x-0 border-t border-gray-200 pt-3"
                  >
                    <div className="pr-5 text-xl font-semibold text-gray-800">
                      {item.label}
                    </div>
                    {outcomeLikertOptions.map((option, index) => (
                      <label
                        key={`${item.name}-${option.value}`}
                        className="flex min-h-[2.75rem] cursor-pointer items-center justify-center"
                        aria-label={`${item.label} ${option.label}`}
                      >
                        <input
                          type="radio"
                          name={item.name}
                          value={option.value}
                          checked={item.value === option.value}
                          className="h-6 w-6"
                          required={index === 0}
                          onChange={(event) => item.setValue(event.target.value)}
                        />
                      </label>
                    ))}
                  </div>
                ))}
              </div>
            </div>

            <div className="flex items-center justify-end">
              <Button
                type="button"
                disabled={!canAdvanceFromLikertPage}
                handleClick={() => setSurveyPage(2)}
              >
                Next
              </Button>
            </div>
          </div>
        ) : (
          <>
            <div>
              <label className={labelClassName}>
                Is there anything else you would like to tell us about your experience in this study?
              </label>
              <textarea
                className={inputClassName}
                rows={6}
                value={generalFeedback}
                onChange={(e) => setGeneralFeedback(e.target.value)}
              />
            </div>

            <div className="flex items-center justify-between">
              <Button type="button" handleClick={() => setSurveyPage(1)}>
                Back
              </Button>
              <Button type="submit">Submit</Button>
            </div>
          </>
        )}
      </form>
      <TestContinueWithoutInteraction onContinue={handleContinueWithoutInteraction} />
    </div>
  );
}
